import type { App } from '../app/App';
import { APP_VERSION } from '../app/version';
import { ensureOnline } from '../networking/session';
import { t } from '../i18n';
import { modal } from './components';
import { h } from './dom';

/**
 * "Suggest an idea": players tell the team what they want in the game (or a
 * bug they met). Messages land in the back-office Inbox, with the screen they
 * came from and the game version.
 */

const MIN = 5;
const MAX = 1000;

export async function openFeedback(app: App): Promise<void> {
  const kinds: [string, string][] = [
    ['idea', t('💡 An idea for the game')],
    ['bug', t('🐞 Something is broken')],
    ['other', t('💬 Something else')],
  ];
  let kind = 'idea';
  const kindRow = h('div', { class: 'feedback-kinds', role: 'radiogroup', 'aria-label': t('What is it about?') });
  const drawKinds = (): void =>
    kindRow.replaceChildren(
      ...kinds.map(([id, label]) =>
        h('button', { type: 'button', class: `feedback-kind ${kind === id ? 'on' : ''}`, role: 'radio', 'aria-checked': String(kind === id), on: { click: () => ((kind = id), drawKinds()) } }, label),
      ),
    );
  drawKinds();
  const text = h('textarea', { class: 'hand-input feedback-text', rows: 5, maxLength: MAX, placeholder: t('What would make the game more fun? What should we add?') }) as HTMLTextAreaElement;
  const count = h('small', { class: 'feedback-count' }, `0 / ${MAX}`);
  const hint = h('p', { class: 'consent-hint', 'aria-live': 'polite' });
  text.addEventListener('input', () => (count.textContent = `${text.value.length} / ${MAX}`));
  const body = h(
    'div',
    { class: 'feedback-body' },
    h('p', null, t('The team reads every message. Thank you for helping make Paper Legends better!')),
    kindRow,
    text,
    count,
    h('p', { class: 'hand-hint' }, t('Please do not write your real name, phone number or address.')),
    hint,
  );
  let sending = false;
  const v = await modal(
    t('Suggest an idea'),
    body,
    [
      { label: t('Cancel'), value: 'close', variant: 'ghost' },
      { label: t('Send'), value: 'send', variant: 'primary', color: 'green' },
    ],
    {
      onOpen: () => text.focus(),
      accept: (value) => {
        if (value !== 'send') return true;
        if (text.value.trim().length < MIN) {
          hint.textContent = t('Write a little more (at least {n} letters).', { n: MIN });
          return false;
        }
        return !sending;
      },
    },
  );
  if (v !== 'send') return;
  sending = true;
  try {
    if (!(await ensureOnline(app, false))) throw new Error(t('Cannot reach the game server. Single-player still works!'));
    await app.api.raw('POST', '/api/feedback', { kind, text: text.value, page: app.scenes.currentName ?? '', version: APP_VERSION });
    app.toast(t('Thanks! The team got your message.'), 'success');
  } catch (err) {
    app.toast(err instanceof Error ? err.message : t('Could not send. Try again later.'), 'error');
  }
}
